interface AddressLinkValues {
  address?: string | null
  address_2?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
  latitude?: number | string | null
  longitude?: number | string | null
  geolocation?: string | null
}

type AddressQuickLinkId = "maps" | "satellite" | "zillow"

interface AddressQuickLink {
  id: AddressQuickLinkId
  href: string
}

interface Coordinates {
  latitude: number
  longitude: number
}

const trimmed = (value?: string | null) => value?.trim() || null

/**
 * Property searches need enough text to identify one address rather than a
 * street or city-wide result. ZIP remains optional because city + state is a
 * complete locality and older records do not always contain it.
 */
const buildCompleteAddress = (values: AddressLinkValues): string | null => {
  const address = trimmed(values.address)
  const city = trimmed(values.city)
  const state = trimmed(values.state)

  if (!address || !city || !state) return null

  return [
    address,
    trimmed(values.address_2),
    city,
    state,
    trimmed(values.zip_code)
  ]
    .filter(Boolean)
    .join(", ")
}

const parseCoordinate = (value: number | string | null | undefined) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  if (!value?.trim()) return null

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const validCoordinates = (
  latitude: number | null,
  longitude: number | null
): Coordinates | null => {
  if (
    latitude === null ||
    longitude === null ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  )
    return null

  return { latitude, longitude }
}

const parseCoordinates = (values: AddressLinkValues): Coordinates | null => {
  const explicit = validCoordinates(
    parseCoordinate(values.latitude),
    parseCoordinate(values.longitude)
  )
  if (explicit) return explicit

  if (!values.geolocation) return null
  const [rawLatitude, rawLongitude, ...extra] = values.geolocation.split(",")
  if (extra.length) return null

  return validCoordinates(
    parseCoordinate(rawLatitude),
    parseCoordinate(rawLongitude)
  )
}

const withParams = (base: string, params: Record<string, string>) => {
  const url = new URL(base)
  Object.entries(params).forEach(([key, value]) =>
    url.searchParams.set(key, value)
  )
  return url.toString()
}

// encodeURIComponent deliberately leaves apostrophes and a few other path
// characters untouched. Encode those as well so addresses such as O'Brien's
// or unit #4 cannot alter Zillow's constructed path.
const encodePathSegment = (value: string) =>
  encodeURIComponent(value).replace(
    /[!'()*]/g,
    (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`
  )

const buildAddressQuickLinks = (
  values: AddressLinkValues
): AddressQuickLink[] => {
  const fullAddress = buildCompleteAddress(values)
  if (!fullAddress) return []

  const coordinates = parseCoordinates(values)
  const coordinateQuery = coordinates
    ? `${coordinates.latitude},${coordinates.longitude}`
    : null

  const mapsUrl = withParams("https://www.google.com/maps/search/", {
    api: "1",
    query: coordinateQuery ?? fullAddress
  })

  // Google documents satellite map URLs with a coordinate center. Its legacy
  // q/t URL is retained as the text fallback for records that predate stored
  // coordinates.
  const satelliteUrl = coordinates
    ? withParams("https://www.google.com/maps/@", {
        api: "1",
        map_action: "map",
        center: `${coordinates.latitude},${coordinates.longitude}`,
        zoom: "18",
        basemap: "satellite"
      })
    : withParams("https://maps.google.com/maps", {
        q: fullAddress,
        t: "k"
      })

  // Zillow does not publish a contractual address-search URL. Keep this
  // constructed path isolated here so it can be adjusted if their routing
  // changes.
  const zillowUrl = `https://www.zillow.com/homes/${encodePathSegment(fullAddress)}_rb/`

  return [
    {
      id: "maps",
      href: mapsUrl
    },
    {
      id: "satellite",
      href: satelliteUrl
    },
    {
      id: "zillow",
      href: zillowUrl
    }
  ]
}

export {
  type AddressLinkValues,
  type AddressQuickLink,
  buildAddressQuickLinks,
  buildCompleteAddress,
  parseCoordinates
}
